import React, { useState, useCallback, useRef } from 'react';
import { FC110, FC111, FC112, FC114, FC115, FC116 } from '../../../services/scApi';
import { dateToInput, inputToDate, dateToParts, partsToDate, fmt } from '../helpers';
import LookupField from '../../../components/LookupField';
import { lookupApi } from '../../../services/lookupApi';
import ColorDetailModal from './ColorDetailModal';
import CommissionDetailModal from './CommissionDetailModal';
import ExpenseDetailModal from './ExpenseDetailModal';
import InspectionSpecModal from './InspectionSpecModal';
import ProductUsageModal from './ProductUsageModal';
import { AcceptanceFieldHint, pwc202f1AcceptanceManifest } from '../../../features/acceptance';

/** Split date input: year (4-digit auto-tab) / month / day */
const DateSplitInput: React.FC<{
  value: string | null | undefined;
  onChange: (isoDate: string | null) => void;
}> = ({ value, onChange }) => {
  const parts = dateToParts(value);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);

  const update = (field: 'year' | 'month' | 'day', v: string) => {
    const next = { ...parts, [field]: v };
    onChange(partsToDate(next.year, next.month, next.day));
  };

  const inputStyle: React.CSSProperties = { height: 28, fontSize: 13, textAlign: 'center', padding: '0 2px', border: '1px solid #d9d9d9', borderRadius: 3 };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <input
        type="text"
        maxLength={4}
        placeholder="YYYY"
        style={{ ...inputStyle, width: 42 }}
        value={parts.year}
        onChange={e => {
          const v = e.target.value.replace(/\D/g, '').slice(0, 4);
          update('year', v);
          if (v.length === 4) {
            monthRef.current?.focus();
            monthRef.current?.select();
          }
        }}
      />
      <span style={{ color: '#999', fontSize: 12 }}>/</span>
      <input
        ref={monthRef}
        type="text"
        maxLength={2}
        placeholder="MM"
        style={{ ...inputStyle, width: 26 }}
        value={parts.month}
        onChange={e => {
          const v = e.target.value.replace(/\D/g, '').slice(0, 2);
          update('month', v);
          if (v.length === 2) {
            dayRef.current?.focus();
            dayRef.current?.select();
          }
        }}
      />
      <span style={{ color: '#999', fontSize: 12 }}>/</span>
      <input
        ref={dayRef}
        type="text"
        maxLength={2}
        placeholder="DD"
        style={{ ...inputStyle, width: 26 }}
        value={parts.day}
        onChange={e => {
          const v = e.target.value.replace(/\D/g, '').slice(0, 2);
          update('day', v);
        }}
      />
    </span>
  );
};

interface Tab2Props {
  formData: Partial<FC110>;
  details: Partial<FC111>[];
  selectedRow: number;
  names: Record<string, string>;
  isEditing: boolean;
  setSelectedRow: (idx: number) => void;
  setDetails: React.Dispatch<React.SetStateAction<Partial<FC111>[]>>;
  getDetailField: (key: keyof FC111) => FC111[keyof FC111];
  setDetailField: (key: keyof FC111, value: FC111[keyof FC111]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
  detailUnitProfitRate: string;
  documentKey?: string;
}

const Tab2ProductDetail: React.FC<Tab2Props> = ({
  formData, details, selectedRow, names, isEditing,
  setSelectedRow, setDetails,
  getDetailField, setDetailField,
  resolveSingleName,
  addDetailRow, deleteDetailRow,
  detailUnitProfitRate, documentKey,
}) => {
  const H = (fieldId: string) => <AcceptanceFieldHint manifest={pwc202f1AcceptanceManifest} fieldId={fieldId} documentKey={documentKey} />;
  const selDetail = details[selectedRow] as Partial<FC111> | undefined;
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [commissionModalOpen, setCommissionModalOpen] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [inspectionModalOpen, setInspectionModalOpen] = useState(false);
  const [usageModalOpen, setUsageModalOpen] = useState(false);

  const handleColorSave = useCallback((colors: Partial<FC115>[]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], colors: colors as FC115[] };
      }
      return next;
    });
    setColorModalOpen(false);
  }, [selectedRow, setDetails]);

  const handleCommissionSave = useCallback((commissions: Partial<FC114>[]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], commissions: commissions as FC114[] };
      }
      return next;
    });
    setCommissionModalOpen(false);
  }, [selectedRow, setDetails]);

  const handleExpenseSave = useCallback((expenses: Partial<FC112>[]) => {
    // BR-C05: aggregate FC112_12 amounts back to FC111_54
    const totalExpense = expenses.reduce((sum, e) => sum + (e.fc112_12 || 0), 0);
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], expenses: expenses as FC112[], fc111_54: totalExpense };
      }
      return next;
    });
    setExpenseModalOpen(false);
  }, [selectedRow, setDetails]);

  const handleInspectionSave = useCallback((inspections: Partial<FC116>[]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], inspections: inspections as FC116[] };
      }
      return next;
    });
    setInspectionModalOpen(false);
  }, [selectedRow, setDetails]);

  const handleUsageSave = useCallback((usage: string, orderType: string) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], fc111_27: usage, fc111_28: orderType };
      }
      return next;
    });
    setUsageModalOpen(false);
  }, [selectedRow, setDetails]);

  return (
    <>
      {/* Grid */}
      <div className="table-wrapper">
        <div className="table-toolbar">
          <div className="left">
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              總項數: <strong>{formData.fc110_30 || details.length}</strong>
            </span>
            <span style={{ fontSize: 12, color: '#ff4d4f', marginLeft: 12 }}>
              * 至少需要一筆產品明細
            </span>
          </div>
          <div className="right">
            <button
              className="btn btn-success btn-sm"
              disabled={!isEditing}
              onClick={addDetailRow}
            >
              + 明細新增
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={!isEditing || details.length <= 1}
              onClick={deleteDetailRow}
            >
              - 明細刪除
            </button>
          </div>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 35 }} data-contract-field="fc111_02">項次</th>
                <th style={{ width: 110 }}><span style={{ color: '#ff4d4f', fontWeight: 700 }}>*</span> 產品代號{H('fc111_10')}</th>
                <th style={{ width: 115 }} data-contract-field="fc111_12"><span style={{ color: '#ff4d4f', fontWeight: 700 }}>*</span> 預計交期{H('fc111_12')}</th>
                <th className="num" style={{ width: 70 }}>銷售數量{H('fc111_13')}</th>
                <th style={{ width: 40 }} data-contract-field="fc111_14">單位{H('fc111_14')}</th>
                <th className="num" style={{ width: 75 }}>單價{H('fc111_15')}</th>
                <th className="num" style={{ width: 80 }}>金額</th>
                <th style={{ width: 30 }} data-contract-field="fc111_57">AS.</th>
              </tr>
            </thead>
            <tbody>
              {details.map((d, i) => (
                <tr
                  key={i}
                  className={selectedRow === i ? 'selected' : ''}
                  onClick={() => setSelectedRow(i)}
                >
                  <td>{d.fc111_02 || i + 1}</td>
                  <td>
                    {isEditing ? (
                      <LookupField lookupType="product"
                        style={{ width: '100%', fontSize: 13 }}
                        value={`${d.fc111_10 || ''}${d.fc111_11 || ''}`}
                        onChange={v => {
                          let c1: string, c2: string;
                          if (v.includes('|')) {
                            [c1, c2] = v.split('|');
                          } else {
                            c1 = v.substring(0, 5); c2 = v.substring(5);
                          }
                          const next = [...details];
                          next[i] = { ...next[i], fc111_10: c1, fc111_11: c2 };
                          setDetails(next);
                        }}
                        onCodeConfirm={code => {
                          let c1: string, c2: string;
                          if (code.includes('|')) { [c1, c2] = code.split('|'); }
                          else { c1 = code.substring(0, 5); c2 = code.substring(5); }
                          if (c1) {
                            lookupApi.getProductInfo(c1, c2 || '').then(info => {
                              if (info.unit) {
                                setDetails(prev => {
                                  const next = [...prev];
                                  if (next[i]) next[i] = { ...next[i], fc111_14: info.unit };
                                  return next;
                                });
                              }
                            }).catch(() => {});
                          }
                        }}
                      />
                    ) : (
                      `${d.fc111_10 || ''}${d.fc111_11 || ''}`
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <DateSplitInput
                        value={d.fc111_12 as string}
                        onChange={val => {
                          const next = [...details];
                          next[i] = { ...next[i], fc111_12: val };
                          setDetails(next);
                        }}
                      />
                    ) : (
                      dateToInput(d.fc111_12 as string)
                    )}
                  </td>
                  <td className="num">
                    {isEditing ? (
                      <input
                        type="number" style={{ width: 60, height: 28, fontSize: 13, textAlign: 'right' }}
                        value={d.fc111_13 || ''}
                        onChange={e => {
                          const qty = parseFloat(e.target.value) || 0;
                          const price = d.fc111_15 || 0;
                          const next = [...details];
                          next[i] = { ...next[i], fc111_13: qty, fc111_16: Math.round(qty * price * 100) / 100 };
                          setDetails(next);
                        }} data-contract-field="fc111_13"
                      />
                    ) : (
                      fmt(d.fc111_13)
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text" style={{ width: 35, height: 28, fontSize: 13 }}
                        value={d.fc111_14 || ''}
                        onChange={e => {
                          const next = [...details];
                          next[i] = { ...next[i], fc111_14: e.target.value };
                          setDetails(next);
                        }} data-contract-field="fc111_14"
                      />
                    ) : (
                      d.fc111_14 || ''
                    )}
                  </td>
                  <td className="num">
                    {isEditing ? (
                      <input
                        type="number" style={{ width: 65, height: 28, fontSize: 13, textAlign: 'right' }}
                        value={d.fc111_15 || ''}
                        onChange={e => {
                          const price = parseFloat(e.target.value) || 0;
                          const qty = d.fc111_13 || 0;
                          const next = [...details];
                          next[i] = { ...next[i], fc111_15: price, fc111_16: Math.round(qty * price * 100) / 100 };
                          setDetails(next);
                        }} data-contract-field="fc111_15"
                      />
                    ) : (
                      fmt(d.fc111_15)
                    )}
                  </td>
                  <td className="num">{fmt(d.fc111_16)}</td>
                  <td>{d.fc111_57 || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* === Detail Panels === */}
      <div className="detail-panels">

        {/* Left: 產品規格 / 包裝 */}
        <div className="detail-panel">
          <div className="panel-title">產品規格與包裝</div>

          <div className="form-row">
            <label data-contract-field="fc111_21">產品規格</label>
            <textarea
              disabled={!isEditing}
              placeholder="輸入產品規格說明"
              value={getDetailField('fc111_21') as string}
              onChange={e => setDetailField('fc111_21', e.target.value)}
            />
          </div>

          <div className="form-row">
              <label className="required">包裝代號{H('fc111_30')}</label>
            <div className="lookup-field">
              <LookupField lookupType="packing" style={{ width: 80 }}
                disabled={!isEditing}
                value={getDetailField('fc111_30') as string}
                onChange={v => setDetailField('fc111_30', v)}
                onCodeConfirm={code => resolveSingleName('packing', 'packing', code)} contractFieldId="fc111_30"
              />
              <span className="lookup-name">{names.packing || '--'}</span>
            </div>
          </div>

          <div className="form-row">
            <label data-contract-field="fc111_301">包裝說明</label>
            <textarea
              disabled={!isEditing}
              placeholder="輸入包裝說明"
              value={getDetailField('fc111_301') as string}
              onChange={e => setDetailField('fc111_301', e.target.value)}
            />
          </div>
        </div>

        {/* Middle: 子模組按鈕 */}
        <div className="detail-panel">
          <div className="panel-title">子模組功能</div>
          <div className="submodule-buttons">
            <button
              className="btn btn-sm"
              disabled={!isEditing || !selDetail?.fc111_13 || selDetail?.fc111_57 !== 'Y'}
              onClick={() => setColorModalOpen(true)}
            >
              顏色明細
            </button>
            <button
              className="btn btn-sm"
              disabled={!isEditing || !selDetail?.fc111_13}
              onClick={() => setCommissionModalOpen(true)}
            >
              銷售佣金
            </button>
            <button
              className="btn btn-sm"
              disabled={!isEditing || !selDetail?.fc111_13}
              onClick={() => setExpenseModalOpen(true)}
            >
              費用明細
            </button>
            <button
              className="btn btn-sm"
              disabled={!isEditing || !selDetail?.fc111_13}
              onClick={() => setInspectionModalOpen(true)}
            >
              檢驗規範
            </button>
            <button
              className="btn btn-sm"
              disabled={!isEditing || !selDetail?.fc111_13}
              onClick={() => setUsageModalOpen(true)}
            >
              產品用途
            </button>
          </div>

          <div className="form-row" style={{ marginTop: 16 }}>
            <label data-contract-field="fc111_55">客戶產品代號</label>
            <input
              type="text"
              disabled={!isEditing}
              value={getDetailField('fc111_55') as string}
              onChange={e => setDetailField('fc111_55', e.target.value)}
            />
          </div>
        </div>

        {/* Right: 預估採購成本 */}
        <div className="detail-panel">
          <div className="cost-card">
            <div className="panel-title">預估採購成本</div>

            <div className="cost-item">
              <label className="required">採購交期{H('fc111_121')}</label>
              <input
                type="date" style={{ width: 140 }}
                disabled={!isEditing}
                value={dateToInput(getDetailField('fc111_121') as string)}
                onChange={e => setDetailField('fc111_121', inputToDate(e.target.value) as string)} data-contract-field="fc111_121"
              />
            </div>
            <div className="cost-item">
              <label className="required">交易條件{H('fc111_29')}</label>
              <div className="lookup-field" style={{ width: 'auto' }}>
                <LookupField lookupType="tradeTerms" style={{ width: 80 }}
                  disabled={!isEditing}
                  value={getDetailField('fc111_29') as string}
                  onChange={v => setDetailField('fc111_29', v)}
                  onCodeConfirm={code => resolveSingleName('tradeTerms', 'tradeTerms', code)} contractFieldId="fc111_29"
                />
                <span className="lookup-name" style={{ minWidth: 40 }}>{names.tradeTerms || '--'}</span>
              </div>
            </div>
            <div className="cost-item">
              <label className="required">採購幣別{H('fc111_41')}</label>
              <div className="lookup-field" style={{ width: 'auto' }}>
                <LookupField lookupType="currency" style={{ width: 80 }}
                  disabled={!isEditing}
                  value={getDetailField('fc111_41') as string}
                  onChange={v => setDetailField('fc111_41', v)}
                  onCodeConfirm={code => resolveSingleName('purchCurrency', 'currency', code)} contractFieldId="fc111_41"
                />
                <span className="lookup-name" style={{ minWidth: 40 }}>{names.purchCurrency || '--'}</span>
              </div>
            </div>
            <div className="cost-item">
              <label>採購匯率{H('fc111_42')}</label>
              <input
                type="text" style={{ width: 120, textAlign: 'right' }}
                readOnly
                value={getDetailField('fc111_42') as string} data-contract-field="fc111_42"
              />
            </div>
            <div className="cost-item">
              <label className="required">採購單價{H('fc111_36')}</label>
              <input
                type="number" style={{ width: 120, textAlign: 'right' }}
                disabled={!isEditing}
                value={getDetailField('fc111_36') as string}
                onChange={e => setDetailField('fc111_36', parseFloat(e.target.value) || 0)}
                step="0.001" data-contract-field="fc111_36"
              />
            </div>
            <div className="cost-item">
              <label data-contract-field="fc111_54">費用金額</label>
              <input
                type="text" style={{ width: 120, textAlign: 'right' }}
                readOnly
                value={getDetailField('fc111_54') as string}
              />
            </div>

            <div style={{ borderTop: '1px dashed #ffe58f', margin: '8px 0', paddingTop: 8 }}>
              <div className="cost-item">
                <label data-contract-field="fc111_51" style={{ fontWeight: 600, color: '#d48806' }}>單位毛利</label>
                <div className="readonly-value">{fmt(selDetail?.fc111_51)}</div>
              </div>
              <div className="cost-item">
                <label data-contract-field="ui_detail_unit_profit_rate" style={{ fontWeight: 600, color: '#d48806' }}>單位毛利率</label>
                <div className="readonly-value">{detailUnitProfitRate} %</div>
              </div>
            </div>
          </div>
        </div>

      </div>{/* /detail-panels */}

      <ColorDetailModal
        key={`${selectedRow}-${colorModalOpen}`}
        open={colorModalOpen}
        onClose={() => setColorModalOpen(false)}
        onSave={handleColorSave}
        colors={selDetail?.colors || []}
        scNo={formData.fc110_01 || ''}
        itemSeq={selDetail?.fc111_02 || 1}
        salesQty={selDetail?.fc111_13 || 0}
      />

      <CommissionDetailModal
        key={`comm-${selectedRow}-${commissionModalOpen}`}
        open={commissionModalOpen}
        onClose={() => setCommissionModalOpen(false)}
        onSave={handleCommissionSave}
        commissions={selDetail?.commissions || []}
        scNo={formData.fc110_01 || ''}
        itemSeq={selDetail?.fc111_02 || 1}
        masterCurrency={formData.fc110_24 || ''}
      />

      <ExpenseDetailModal
        key={`exp-${selectedRow}-${expenseModalOpen}`}
        open={expenseModalOpen}
        onClose={() => setExpenseModalOpen(false)}
        onSave={handleExpenseSave}
        expenses={selDetail?.expenses || []}
        scNo={formData.fc110_01 || ''}
        itemSeq={selDetail?.fc111_02 || 1}
      />

      <InspectionSpecModal
        key={`insp-${selectedRow}-${inspectionModalOpen}`}
        open={inspectionModalOpen}
        onClose={() => setInspectionModalOpen(false)}
        onSave={handleInspectionSave}
        inspections={selDetail?.inspections || []}
        scNo={formData.fc110_01 || ''}
        itemSeq={selDetail?.fc111_02 || 1}
      />

      <ProductUsageModal
        key={`usage-${selectedRow}-${usageModalOpen}`}
        open={usageModalOpen}
        onClose={() => setUsageModalOpen(false)}
        onSave={handleUsageSave}
        currentUsage={selDetail?.fc111_27 || ''}
        currentOrderType={selDetail?.fc111_28 || ''}
        itemSeq={selDetail?.fc111_02 || 1}
      />
    </>
  );
};

export default React.memo(Tab2ProductDetail);
